import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useSwiftaidAuthorisationStatus } from '@/composables/useSwiftaidAuthorisationStatus'
import { useDashboardWelcomeWizard } from '@/composables/useDashboardWelcomeWizard'
import { useDonorPlan } from '@/composables/useDonorPlan'
import { useClaimTaxBack } from '@/composables/useClaimTaxBack'
import {
  claimPromptDonorKey,
  isClaimTaxBackPromptDismissed,
  persistClaimTaxBackPromptDismissed,
} from '@/helpers/claimTaxBack/promptDismiss'

// Bumped on dismiss so every notifications consumer hides the card together.
const claimPromptDismissVersion = ref(0)

/**
 * Actionable dashboard notifications and Swiftaid status setup.
 * Used for the home notifications section heading indicator and mobile menu dot.
 */
export function useDashboardNotifications() {
  const store = useStore()
  const { isAuthorised, isStatusKnown } = useSwiftaidAuthorisationStatus()
  const { showWelcomeNotification } = useDashboardWelcomeWizard()
  const { isPaid, isPlanKnown } = useDonorPlan()
  const { isClaimAvailable, hasCompletedChecker, showNewBadge } =
    useClaimTaxBack()

  const donorKey = computed(() =>
    claimPromptDonorKey(store.getters.user?.emailAddress)
  )
  const claimPromptDismissed = computed(() => {
    // Read the version so dismiss updates every notifications consumer.
    const dismissVersion = claimPromptDismissVersion.value
    return (
      isClaimTaxBackPromptDismissed(donorKey.value) && dismissVersion >= 0
    )
  })

  const dismissClaimTaxBackPrompt = () => {
    persistClaimTaxBackPromptDismissed(donorKey.value)
    claimPromptDismissVersion.value += 1
  }

  const globalError = computed(() => Boolean(store.getters.globalError))
  const hasLinkedAccounts = computed(() =>
    Boolean(store.state.accounts.hasAccounts)
  )
  // Avoid “no linked accounts” flash before GET /me has populated the profile.
  const profileReady = computed(() =>
    Boolean(store.getters.user?.emailAddress?.trim())
  )

  // Only treat as needing attention once the auth status fetch has settled.
  const needsAuthorisation = computed(
    () => isStatusKnown.value && !isAuthorised.value
  )
  const needsLinkedAccount = computed(
    () => profileReady.value && !hasLinkedAccounts.value
  )

  /** Grouped Swiftaid status notification when authorisation or linked accounts need attention. */
  const showSwiftaidStatusNotification = computed(
    () =>
      !globalError.value &&
      (needsAuthorisation.value || needsLinkedAccount.value)
  )

  /**
   * Claim tax back intrigue prompt: free donors only (Bob never sees an
   * upsell), until they run the checker or dismiss the card.
   */
  const showClaimTaxBackNotification = computed(
    () =>
      !globalError.value &&
      isPlanKnown.value &&
      !isPaid.value &&
      isClaimAvailable.value &&
      !hasCompletedChecker.value &&
      !claimPromptDismissed.value
  )

  /**
   * Paid discovery with no price. Same slot as the free prompt; it stays
   * until they open Claim tax back (which sets firstVisitedAt) or dismiss.
   */
  const showClaimTaxBackPaidNotification = computed(
    () =>
      !globalError.value &&
      isPlanKnown.value &&
      isPaid.value &&
      isClaimAvailable.value &&
      showNewBadge.value &&
      !claimPromptDismissed.value
  )

  const hasVisibleNotifications = computed(
    () =>
      showWelcomeNotification.value ||
      showSwiftaidStatusNotification.value ||
      showClaimTaxBackNotification.value ||
      showClaimTaxBackPaidNotification.value
  )

  /** Number of visible notification cards (grouped, not per pending donation). */
  const actionCount = computed(() => {
    let count = 0
    if (showWelcomeNotification.value) {
      count += 1
    }
    if (showSwiftaidStatusNotification.value) {
      count += 1
    }
    if (showClaimTaxBackNotification.value) {
      count += 1
    }
    if (showClaimTaxBackPaidNotification.value) {
      count += 1
    }
    return count
  })

  return {
    showWelcomeNotification,
    showSwiftaidStatusNotification,
    showClaimTaxBackNotification,
    showClaimTaxBackPaidNotification,
    dismissClaimTaxBackPrompt,
    needsAuthorisation,
    needsLinkedAccount,
    hasVisibleNotifications,
    actionCount,
  }
}
